import { FormEvent, useEffect, useState } from "react";
import { useToast } from "#/components/common/ToastProvider";

import type {
  CarryForwardExpiryMode,
  LeaveApplicableGender,
  LeaveDayCountMode,
  LeaveType,
  LeaveTypeCreateRequest,
} from "../types";

type Props = {
  initialValue?: LeaveType | null;
  submitting?: boolean;
  onSubmit: (payload: LeaveTypeCreateRequest) => Promise<void> | void;
  onCancel?: () => void;
};

export function LeaveTypeForm({
  initialValue,
  submitting,
  onSubmit,
  onCancel,
}: Props) {
  const toast = useToast();
  const [form, setForm] = useState<LeaveTypeCreateRequest>({
    code: "",
    name: "",
    description: "",
    paid: true,
    requiresBalance: true,
    allowsCarryForward: false,
    maxCarryForwardDays: null,
    carryForwardExpiryMode: "NONE",
    carryForwardExpiryMonthsAfterYearEnd: null,
    annualEntitlementDays: 0,
    maxDaysPerRequest: null,
    requiresAttachment: false,
    active: true,
    dayCountMode: "WORKING_DAYS",
    excludeWeekends: true,
    excludePublicHolidays: true,
    requiresReliefEmployee: false,
    minimumNoticeDays: null,
    applicableGender: "ALL",
    allowUnmatchedEmployees: false,
    showInDashboard: false,
  });

  useEffect(() => {
    if (initialValue) {
      setForm({
        code: initialValue.code,
        name: initialValue.name,
        description: initialValue.description || "",
        paid: initialValue.paid,
        requiresBalance: initialValue.requiresBalance,
        allowsCarryForward: initialValue.allowsCarryForward,
        maxCarryForwardDays: initialValue.maxCarryForwardDays ?? null,
        carryForwardExpiryMode: initialValue.carryForwardExpiryMode || "NONE",
        carryForwardExpiryMonthsAfterYearEnd:
          initialValue.carryForwardExpiryMonthsAfterYearEnd ?? null,
        annualEntitlementDays: initialValue.annualEntitlementDays ?? 0,
        maxDaysPerRequest: initialValue.maxDaysPerRequest ?? null,
        requiresAttachment: initialValue.requiresAttachment,
        active: initialValue.active,
        dayCountMode: initialValue.dayCountMode || "WORKING_DAYS",
        excludeWeekends: initialValue.excludeWeekends ?? true,
        excludePublicHolidays: initialValue.excludePublicHolidays ?? true,
        requiresReliefEmployee: initialValue.requiresReliefEmployee ?? false,
        minimumNoticeDays: initialValue.minimumNoticeDays ?? null,
        applicableGender: initialValue.applicableGender || "ALL",
        allowUnmatchedEmployees: initialValue.allowUnmatchedEmployees ?? false,
        showInDashboard: initialValue.showInDashboard ?? false,
      });
    }
  }, [initialValue]);

  function set<K extends keyof LeaveTypeCreateRequest>(
    key: K,
    value: LeaveTypeCreateRequest[K],
  ) {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    const carryForwardExpiryMode =
      form.allowsCarryForward ? (form.carryForwardExpiryMode || "NONE") : "NONE";

    if (
      form.allowsCarryForward &&
      carryForwardExpiryMode === "MONTHS_AFTER_YEAR_END" &&
      (!form.carryForwardExpiryMonthsAfterYearEnd ||
        Number(form.carryForwardExpiryMonthsAfterYearEnd) < 1)
    ) {
      toast.error(
        "Please enter how many months after year end the carry-forward expires (minimum 1).",
      );
      return;
    }

    await onSubmit({
      ...form,
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      description: form.description?.trim() || null,
      annualEntitlementDays: form.requiresBalance
        ? Number(form.annualEntitlementDays || 0)
        : null,
      maxDaysPerRequest:
        form.maxDaysPerRequest === null || form.maxDaysPerRequest === undefined
          ? null
          : Number(form.maxDaysPerRequest),
      dayCountMode: form.dayCountMode || "WORKING_DAYS",
      excludeWeekends: Boolean(form.excludeWeekends),
      excludePublicHolidays: Boolean(form.excludePublicHolidays),
      requiresReliefEmployee: Boolean(form.requiresReliefEmployee),
      minimumNoticeDays:
        form.minimumNoticeDays === null || form.minimumNoticeDays === undefined
          ? null
          : Number(form.minimumNoticeDays),
      applicableGender: form.applicableGender || "ALL",
      allowUnmatchedEmployees: Boolean(form.allowUnmatchedEmployees),
      showInDashboard: Boolean(form.showInDashboard),
      allowsCarryForward: Boolean(form.allowsCarryForward),
      maxCarryForwardDays:
        form.allowsCarryForward && form.maxCarryForwardDays
          ? Number(form.maxCarryForwardDays)
          : null,
      carryForwardExpiryMode,
      carryForwardExpiryMonthsAfterYearEnd:
        form.allowsCarryForward &&
        carryForwardExpiryMode === "MONTHS_AFTER_YEAR_END"
          ? Number(form.carryForwardExpiryMonthsAfterYearEnd)
          : null,
    });
  }

  const genderValue = form.applicableGender || "ALL";
  const isGenderRestricted = genderValue !== "ALL";

  return (
    <form
      onSubmit={submit}
      className="grid gap-4 rounded-3xl border bg-white p-5 shadow-sm md:grid-cols-2"
    >
      <Field label="Code" required>
        <input
          className="input"
          value={form.code}
          onChange={(event) => set("code", event.target.value)}
          required
          placeholder="ANNUAL"
        />
      </Field>

      <Field label="Name" required>
        <input
          className="input"
          value={form.name}
          onChange={(event) => set("name", event.target.value)}
          required
          placeholder="Annual Leave"
        />
      </Field>

      <div className="md:col-span-2">
        <Field label="Description">
          <textarea
            className="input min-h-24"
            value={form.description || ""}
            onChange={(event) => set("description", event.target.value)}
            placeholder="Optional description"
          />
        </Field>
      </div>

      {/* Gender restriction — prominent section */}
      <div className="md:col-span-2">
        <div className="rounded-2xl border p-4">
          <div className="mb-3 text-sm font-black text-slate-800">Gender Restriction</div>

          <div className="flex flex-wrap gap-3">
            {(
              [
                { value: "ALL", label: "All Employees", desc: "No gender restriction" },
                { value: "FEMALE", label: "Female Only", desc: "e.g. Maternity Leave" },
                { value: "MALE", label: "Male Only", desc: "e.g. Paternity Leave" },
              ] as const
            ).map((opt) => (
              <label
                key={opt.value}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${
                  genderValue === opt.value
                    ? opt.value === "FEMALE"
                      ? "border-pink-400 bg-pink-50"
                      : opt.value === "MALE"
                        ? "border-blue-400 bg-blue-50"
                        : "border-emerald-400 bg-emerald-50"
                    : "hover:bg-slate-50"
                }`}
              >
                <input
                  type="radio"
                  name="applicableGender"
                  value={opt.value}
                  checked={genderValue === opt.value}
                  onChange={() => set("applicableGender", opt.value as LeaveApplicableGender)}
                  className="h-4 w-4"
                />
                <div>
                  <div className="font-bold">{opt.label}</div>
                  <div className="text-xs text-slate-500">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>

          {isGenderRestricted && (
            <div
              className={`mt-3 rounded-xl px-4 py-2 text-sm font-semibold ${
                genderValue === "FEMALE"
                  ? "bg-pink-100 text-pink-800"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {genderValue === "FEMALE"
                ? "♀ This leave type is visible and applicable to female employees only."
                : "♂ This leave type is visible and applicable to male employees only."}
            </div>
          )}
        </div>
      </div>

      <Field label="Day Count Mode">
        <select
          className="input"
          value={form.dayCountMode || "WORKING_DAYS"}
          onChange={(event) =>
            set("dayCountMode", event.target.value as LeaveDayCountMode)
          }
        >
          <option value="WORKING_DAYS">Working Days</option>
          <option value="CALENDAR_DAYS">Calendar Days</option>
        </select>
      </Field>

      <Field label="Default Annual Entitlement Days">
        <input
          type="number"
          className="input"
          min={0}
          step="0.5"
          disabled={!form.requiresBalance}
          value={form.annualEntitlementDays ?? ""}
          onChange={(event) =>
            set(
              "annualEntitlementDays",
              event.target.value ? Number(event.target.value) : 0,
            )
          }
        />
      </Field>

      <Field label="Default Max Days Per Request">
        <input
          type="number"
          className="input"
          min={0}
          step="0.5"
          value={form.maxDaysPerRequest ?? ""}
          onChange={(event) =>
            set(
              "maxDaysPerRequest",
              event.target.value ? Number(event.target.value) : null,
            )
          }
        />
      </Field>

      <Field label="Minimum Notice Days">
        <input
          type="number"
          className="input"
          min={0}
          value={form.minimumNoticeDays ?? ""}
          onChange={(event) =>
            set(
              "minimumNoticeDays",
              event.target.value ? Number(event.target.value) : null,
            )
          }
        />
      </Field>

      <Check
        label="Paid Leave"
        checked={Boolean(form.paid)}
        onChange={(value) => set("paid", value)}
      />

      <Check
        label="Requires Balance"
        checked={Boolean(form.requiresBalance)}
        onChange={(value) => set("requiresBalance", value)}
      />

      <Check
        label="Requires Attachment"
        checked={Boolean(form.requiresAttachment)}
        onChange={(value) => set("requiresAttachment", value)}
      />

      {form.dayCountMode === "CALENDAR_DAYS" ? (
        <div className="rounded-xl bg-slate-50 px-4 py-2.5 text-xs text-slate-500">
          Calendar Days counts every day in the range, including weekends and
          holidays — "Exclude Weekends"/"Exclude Public Holidays" only apply
          in Working Days mode.
        </div>
      ) : (
        <>
          <Check
            label="Exclude Weekends"
            checked={Boolean(form.excludeWeekends)}
            onChange={(value) => set("excludeWeekends", value)}
          />

          <Check
            label="Exclude Public Holidays"
            checked={Boolean(form.excludePublicHolidays)}
            onChange={(value) => set("excludePublicHolidays", value)}
          />
        </>
      )}

      <Check
        label="Requires Relief Employee"
        checked={Boolean(form.requiresReliefEmployee)}
        onChange={(value) => set("requiresReliefEmployee", value)}
      />

      <Check
        label="Active"
        checked={Boolean(form.active)}
        onChange={(value) => set("active", value)}
      />

      {/* Carry-forward section */}
      <div className="md:col-span-2">
        <div className="rounded-2xl border p-4">
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={Boolean(form.allowsCarryForward)}
              onChange={(event) => set("allowsCarryForward", event.target.checked)}
            />
            <div>
              <div className="text-sm font-black text-slate-800">
                Allow Carry-Forward
              </div>
              <div className="text-xs text-slate-500">
                Unused days from this year roll over into the next leave year.
              </div>
            </div>
          </label>

          {form.allowsCarryForward && (
            <div className="mt-4 grid gap-4 border-t pt-4 md:grid-cols-3">
              <Field label="Max Carry-Forward Days">
                <input
                  type="number"
                  className="input"
                  min={0}
                  step="0.5"
                  value={form.maxCarryForwardDays ?? ""}
                  placeholder="Unlimited if blank"
                  onChange={(event) =>
                    set(
                      "maxCarryForwardDays",
                      event.target.value ? Number(event.target.value) : null,
                    )
                  }
                />
              </Field>

              <Field label="Expiry Mode">
                <select
                  className="input"
                  value={form.carryForwardExpiryMode || "NONE"}
                  onChange={(event) =>
                    set(
                      "carryForwardExpiryMode",
                      event.target.value as CarryForwardExpiryMode,
                    )
                  }
                >
                  <option value="NONE">Never expire</option>
                  <option value="MONTHS_AFTER_YEAR_END">
                    Expire N months after year end
                  </option>
                </select>
              </Field>

              {form.carryForwardExpiryMode === "MONTHS_AFTER_YEAR_END" && (
                <Field label="Months After Year End" required>
                  <input
                    type="number"
                    className="input"
                    min={1}
                    value={form.carryForwardExpiryMonthsAfterYearEnd ?? ""}
                    placeholder="e.g. 3"
                    required
                    onChange={(event) =>
                      set(
                        "carryForwardExpiryMonthsAfterYearEnd",
                        event.target.value ? Number(event.target.value) : null,
                      )
                    }
                  />
                </Field>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Employee Eligibility Fallback */}
      <div className="md:col-span-2">
        <div className={`rounded-2xl border p-4 ${form.allowUnmatchedEmployees ? "border-blue-200 bg-blue-50" : ""}`}>
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={Boolean(form.allowUnmatchedEmployees)}
              onChange={(e) => set("allowUnmatchedEmployees", e.target.checked)}
            />
            <div>
              <div className="text-sm font-black text-slate-800">
                Allow Unmatched Employees (Open to All)
              </div>
              <div className="text-xs text-slate-500">
                Employees who don't match any entitlement rule still see this leave type
                and receive the default annual entitlement days above.
              </div>
            </div>
          </label>

          {form.allowUnmatchedEmployees && (
            <div className="mt-3 space-y-2 border-t pt-3">
              <div className="rounded-xl bg-blue-100 px-4 py-2.5 text-xs text-blue-800">
                <strong>When to use:</strong> Leave types like Annual Leave or Sick Leave that
                should be available to all employees regardless of employment type, category, or
                other entitlement-rule criteria. Employees with no matching rule fall through to
                the default <strong>{form.annualEntitlementDays ?? 0} day(s)</strong> set above.
              </div>
              {form.applicableGender !== "ALL" && (
                <div className="rounded-xl bg-amber-100 px-4 py-2.5 text-xs text-amber-800">
                  ⚠️ Gender restriction is still enforced — only{" "}
                  {form.applicableGender === "FEMALE" ? "female" : "male"} employees without a
                  matching rule will fall through to the default entitlement.
                </div>
              )}
            </div>
          )}

          {!form.allowUnmatchedEmployees && (
            <p className="mt-2 text-xs text-slate-400">
              When off, employees with no matching entitlement rule will not see this leave type at all.
              Use this for targeted leave types like Maternity or Paternity Leave.
            </p>
          )}
        </div>
      </div>

      {/* Show on Dashboard */}
      <div className="md:col-span-2">
        <div className={`rounded-2xl border p-4 ${form.showInDashboard ? "border-emerald-200 bg-emerald-50" : ""}`}>
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={Boolean(form.showInDashboard)}
              onChange={(e) => set("showInDashboard", e.target.checked)}
            />
            <div>
              <div className="text-sm font-black text-slate-800">
                Show on Employee Dashboard
              </div>
              <div className="text-xs text-slate-500">
                This leave type's balance will appear in the "My Leave Balance" card on
                the employee dashboard. Enable for planned leave (e.g. Annual Leave).
                Leave off for reactive leave like Sick Leave.
              </div>
            </div>
          </label>
          {form.showInDashboard && (
            <div className="mt-3 rounded-xl bg-emerald-100 px-4 py-2.5 text-xs text-emerald-800">
              Employees will see this leave type's remaining balance on their dashboard homepage.
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 md:col-span-2">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
          >
            Cancel
          </button>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {submitting
            ? "Saving..."
            : initialValue
              ? "Update Leave Type"
              : "Create Leave Type"}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2">
      <div className="text-sm font-semibold text-slate-700">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </div>

      {children}
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border p-4 text-sm font-semibold text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4"
      />

      {label}
    </label>
  );
}
